import React, {Dispatch, SetStateAction, useCallback, useMemo} from 'react';

import {
    getAllActionTypes,
    getAllCategories,
    getFilteredActionTypes,
    getFilteredCategories,
} from 'src/components/change-log/utils';
import {DateRangeInput} from 'src/components/input';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import ComboBox, {ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import type {AccountAuditLogCriteria} from '../typedef';
import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';


type Props = {
    actionTypeDictionary: DictionaryTree[],
    filter?: AccountAuditLogCriteria,
    onChange?: Dispatch<SetStateAction<AccountAuditLogCriteria>>,
};

export const LogFiltersBody = ({filter, onChange, actionTypeDictionary}: Props) => {

    const setStartDate = useCallback((date: string) => {
        if (onChange) {
            if (date) {
                onChange(prev => ({...prev, startDate: date}));
            } else {
                onChange(prev => {
                    const {startDate, ...rest} = prev;
                    return rest;
                });
            }
        }
    }, [onChange]);


    const setEndDate = useCallback((date: string) => {
        if (onChange) {
            if (date) {
                onChange(prev => ({...prev, endDate: date}));
            } else {
                onChange(prev => {
                    const {endDate, ...rest} = prev;
                    return rest;
                });
            }
        }
    }, [onChange]);

    const setActionType = useCallback((action: string) => {
        if (onChange) {
            if (action) {
                onChange(prev => ({...prev, actionType: action}));
            } else {
                onChange(prev => {
                    const {actionType, ...rest} = prev;
                    return rest;
                });
            }
        }
    }, [onChange]);

    const setCategories = useCallback((categoryIds: string[]) => {
        if (onChange) {
            if (categoryIds.length) {
                onChange(prev => ({...prev, categories: categoryIds}));
            } else {
                onChange(prev => {
                    const {categories, ...rest} = prev;
                    return rest;
                });
            }
        }
    }, [onChange]);

    const filteredCategories = useMemo(() => {
        if (filter?.actionType) {
            return getFilteredCategories(actionTypeDictionary, filter.actionType);
        } else {
            return getAllCategories(actionTypeDictionary);
        }
    }, [filter, actionTypeDictionary]);

    const filteredActionTypes = useMemo(() => {
        if (filter?.categories) {
            return getFilteredActionTypes(actionTypeDictionary, filter.categories);
        } else {
            return getAllActionTypes(actionTypeDictionary);
        }
    }, [filter, actionTypeDictionary]);

    useEffectWithDeps((prev, current) => {
        if (prev.filter?.actionType !== current.filter?.actionType && current.filter?.actionType) {
            const matchedType = current.filteredActionTypes.find(({code}) => code === current.filter?.actionType);
            if (!matchedType) {
                current.setActionType('');
            }

            const matchedCategories = current.filteredCategories.map(({code}) => code);
            current.setCategories(matchedCategories);
        }
    }, {filter, filteredActionTypes, setActionType, filteredCategories, setCategories});

    return (
        <Group direction="v" gap="s">
            <Label>Category</Label>
            <div style={{width: 415}}>
                <ComboBox
                    value={filter?.categories || []}
                    onChange={setCategories}
                    multiple={true}
                    placeholder="All Categories">
                    {filteredCategories.map(({code, description}) => (
                        <ComboboxItem key={code} value={code} caption={description}/>
                    ))}
                </ComboBox>
            </div>
            <Label>Action Type</Label>
            <div style={{width: 415}}>
                <ComboBox value={filter?.actionType || ''} onChange={setActionType}>
                    <DefaultItem value="" key="all" caption="All"/>
                    {filteredActionTypes.map(({code, description}) => (
                        <ComboboxItem key={code} value={code} caption={description}/>
                    ))}
                </ComboBox>
            </div>
            <Label>Period</Label>
            <Group direction="h">
                <DateRangeInput
                    id="change-log-start-date-picker"
                    width={200}
                    label="Starts"
                    clearable={true}
                    startDate={filter?.startDate}
                    endDate={filter?.endDate}
                    preventFutureDate={true}
                    selectRangeType="start"
                    onChange={setStartDate}
                />
                <DateRangeInput
                    id="change-log-end-date-picker"
                    width={200}
                    label={<>Ends&nbsp;&nbsp;</>}
                    clearable={true}
                    startDate={filter?.startDate}
                    endDate={filter?.endDate}
                    preventFutureDate={true}
                    selectRangeType="end"
                    onChange={setEndDate}
                />
            </Group>
        </Group>
    );
};
